import HudRadioButton from "app/game/HudRadioButton";
import RotateButton from "app/game/RotateButton";
import ZoomButton from "app/game/ZoomButton";
import { GODOT_MESSAGE, sendGodotMessage } from "app/godot-embed/godot-bridge";
import IconArrow from "app/icons/IconArrow";
import IconBrush from "app/icons/IconBrush";
import IconX from "app/icons/IconX";
import useTutorialState, {
  TutorialAction,
  useTutorialActionDispatch,
} from "app/tutorial/useTutorialState";
import HudButton from "app/ui/HudButton";
import cn from "app/util/classnames";
import { useCallback, useState } from "react";

const ICON_SIZE = 24;

interface ButtonPanelProps {
  isLandscape: boolean;
  hide: boolean;
  allowZoomAndRotate: boolean;
  showBackButton: boolean;
  disableToolButtons: boolean;
  onBackButtonClick: () => void;
}

// Button panel used in gameplay
export default function ButtonPanel(props: ButtonPanelProps) {
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  const { phaseData: tutorialPhase } = useTutorialState();
  const tutorialDispatch = useTutorialActionDispatch();

  const handleZoomClick = useCallback(() => {
    if (props.hide) return;
    setIsZoomedIn((prev) => !prev);
    sendGodotMessage(GODOT_MESSAGE.TOGGLE_ZOOM);
  }, [props.hide]);

  const handleToolSelectionChange = useCallback(
    (selection: number) => {
      if (props.hide) return;

      switch (selection) {
        case 0:
          sendGodotMessage(GODOT_MESSAGE.SET_TOOL_X);
          break;
        case 1:
          sendGodotMessage(GODOT_MESSAGE.SET_TOOL_COLOR);
          break;
      }

      if (tutorialPhase?.waitFor === "toolSwitch") {
        tutorialDispatch({ type: TutorialAction.ADVANCE_TUTORIAL });
      }
    },
    [props.hide, tutorialPhase, tutorialDispatch],
  );

  function handleBackToResultsClick() {
    props.onBackButtonClick();
  }

  return (
    <div
      className={cn(
        "shrink",
        "flex justify-center gap-2",
        "py-1 pb-2",
        props.isLandscape ? "flex-col" : "flex-row",
        props.hide ? "opacity-0" : "opacity-100",
        "transition-opacity duration-300 transform-gpu",
      )}
    >
      {props.showBackButton ? (
        <div>
          <div className="flex flex-col items-center p-1 bg-green-mid rounded-sm">
            <HudButton // Back button (for returning to GameOver)
              onClick={handleBackToResultsClick}
            >
              <IconArrow width={24} height={24} rotate={180} />
            </HudButton>
            <p // Button label
              className="uppercase text-xs"
            >
              Return
            </p>
          </div>
        </div>
      ) : (
        <>
          {props.allowZoomAndRotate && !props.isLandscape && (
            <>
              <div // Rotate button
                className={cn(
                  "rounded-sm bg-green-dark",
                  !isZoomedIn ? "opacity-0" : "opacity-100",
                  "transition-opacity duration-200",
                )}
              >
                <RotateButton disabled={!isZoomedIn} />
              </div>
              <div // Zoom button
                className={cn("rounded-sm bg-green-dark")}
              >
                <ZoomButton isZoomed={isZoomedIn} onClick={handleZoomClick} />
              </div>
            </>
          )}
          <HudRadioButton // Tool selector
            onSelectionChange={handleToolSelectionChange}
            button0Icon={<IconBrush width={ICON_SIZE} height={ICON_SIZE} />}
            button0Label={"Color"}
            button1Icon={<IconX width={ICON_SIZE} height={ICON_SIZE} />}
            button1Label={"X Out"}
            useVerticalLayout={props.isLandscape}
            keyboardShortcut=" "
            disable={props.disableToolButtons}
            highlightColorButton={tutorialPhase?.highlightColorButton ?? false}
            highlightXButton={tutorialPhase?.highlightXOutButton ?? false}
          />
        </>
      )}
    </div>
  );
}
